import './assets/main.css'
import '@mdi/font/css/materialdesignicons.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueKonva from 'vue-konva'
import * as Sentry from '@sentry/vue'
import posthog from 'posthog-js'

import App from './App.vue'
import router from './router'
import { vuetify } from './plugins/vuetify'

import initializeInterceptors, { isExpectedSessionEndError } from './services/interceptors'
import {
  markUpdateAvailable,
  setServiceWorkerUpdater,
  startAppVersionWatcher,
} from './composables/useAppVersionCheck'
import { syncSentryUserFromAccessToken } from './utils/sentryUser'
import { applyAxiosTimeoutContextToSentryEvent } from './utils/axiosTimeoutContext'
import { registerSW } from 'virtual:pwa-register'

initializeInterceptors()

// Prompt mode keeps the new worker waiting so this page stays on the previous
// precache. onNeedRefresh shows the banner. After the user confirms, the new
// worker takes control and every open tab reloads (the plugin default).
const updateServiceWorker = registerSW({
  immediate: true,
  onNeedRefresh() {
    markUpdateAvailable()
  },
})
setServiceWorkerUpdater(updateServiceWorker)
startAppVersionWatcher()

const app = createApp(App)

const posthogProjectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN
const posthogHost = import.meta.env.VITE_POSTHOG_HOST

if (posthogProjectToken && posthogHost) {
  posthog.init(posthogProjectToken, {
    api_host: posthogHost,
  })
} else if (import.meta.env.DEV) {
  const missingVariable = posthogProjectToken
    ? 'VITE_POSTHOG_HOST'
    : 'VITE_POSTHOG_PROJECT_TOKEN'

  throw new Error(
    `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
  )
}

function parseTracesSampleRate(): number {
  const raw = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
  if (raw === undefined || raw === '') {
    return 0.05
  }
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) ? parsed : 0.05
}

function buildTracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = []
  const apiUrl = import.meta.env.VITE_API_URL?.trim()
  if (apiUrl) {
    targets.push(apiUrl)
  }
  if (import.meta.env.DEV) {
    targets.push('localhost')
  }
  return targets
}

Sentry.init({
  app,
  dsn: 'https://cf507a791e1509e616c6cc6be4a8b5c5@o4511635220529152.ingest.us.sentry.io/4511635228983296',
  integrations: [Sentry.browserTracingIntegration({ router })],
  tracePropagationTargets: buildTracePropagationTargets(),
  tracesSampleRate: parseTracesSampleRate(),
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: []
  },
  // Logs
  enableLogs: true,
  environment: import.meta.env.VITE_NODE_ENV,
  beforeSend(event, hint) {
    if (isExpectedSessionEndError(hint.originalException)) {
      return null
    }

    const originalException = hint.originalException as {
      response?: { status?: number; config?: { url?: string } }
      isExpectedSessionEnd?: boolean
    } | undefined

    if (
      originalException?.response?.status === 401
      && originalException?.response?.config?.url?.includes('/auth/refresh')
    ) {
      return null
    }

    return applyAxiosTimeoutContextToSentryEvent(event, hint.originalException)
  },
})

const sentryErrorHandler = app.config.errorHandler

app.config.errorHandler = (err, instance, info) => {
  if (posthogProjectToken && posthogHost) {
    posthog.captureException(err)
  }

  sentryErrorHandler?.(err, instance, info)
}

function readAccessTokenFromStorage(): string {
  try {
    return localStorage?.getItem('accessToken') || ''
  } catch {
    return ''
  }
}

syncSentryUserFromAccessToken(readAccessTokenFromStorage())

app.use(vuetify)
app.use(createPinia())
app.use(router)
app.use(VueKonva)

app.mount('#app')
